import { Box, Flex, Spinner } from '@chakra-ui/react';
import { forwardRef, useImperativeHandle, useRef, useMemo } from 'react';
import { FormBuilder } from '@/components/formBuilder';
import { KButton, KText } from '@/ui-library/components';
import { ApprovalFlowSteps } from '@/components/ats/ApprovalFlowSteps/ApprovalFlowSteps';
import {
  useGetApprovalsByEntityQuery,
  // useValidatePreapprovalMutation,
} from '../api';
import type {
  OfferApprovalFormValues,
  OfferApprovalRef,
  OfferApprovalProps,
} from './offer-approvals/OfferApproval.interface';
import { PlusCircle } from '@kula-ai/icons/dual-tone/PlusCircle';
import { extractScorecards } from '@/components/contacts/candidate-job-drawer/tabs/application-scorecard/helper';
import { ScorecardTableExtension } from './offer-approvals/ScorecardTableExtension';
import { useOfferStore } from '../store';
import { useShowToast } from '@/hooks';

export const OfferApproval = forwardRef<OfferApprovalRef, OfferApprovalProps>(
  (props, comRef) => {
    const offerId = useOfferStore((state) => state.offerId);
    const applicationData = useOfferStore((state) => state.applicationData);
    const toast = useShowToast();
    const {
      entityId = offerId,
      entityType = 'offer',
      currentOffer = {},
    } = props;
    const formRef = useRef<any>(null);
    const editorInstanceRef = useRef<any>(null);

    const { data: approvalData, isLoading: isApprovalLoading } =
      useGetApprovalsByEntityQuery({
        entityId,
        entityType,
      });

    useImperativeHandle(comRef, () => ({
      getModel: async () => {
        const formValues = formRef.current?.values as OfferApprovalFormValues;
        const errors = await formRef.current?.validateForm();

        const hasValidData = !errors || Object.keys(errors).length === 0;

        // Get editor instance and convert content to HTML
        const editor = formValues?.editorInstance || editorInstanceRef.current;
        const notesHtml = editor?.getHTML?.() || '';
        return {
          hasValidData,
          payload: {
            note: notesHtml,
          },
        };
      },
    }));

    const initialValues: OfferApprovalFormValues = useMemo(
      () => ({
        note: currentOffer?.approval_request_note || '',
      }),
      [currentOffer]
    );

    const handleInsertScorecardHistory = () => {
      const editor =
        formRef.current?.values?.editorInstance || editorInstanceRef.current;

      if (!editor) {
        console.error('Editor instance not available');
        return;
      }

      // Check if a scorecard table already exists
      let hasTable = false;
      editor.state.doc.descendants((node: any) => {
        if (node.type.name === 'ScorecardTable') {
          hasTable = true;
          return false; // Stop traversing
        }
      });

      if (hasTable) {
        return;
      }

      const { completed } = extractScorecards(applicationData || {});

      if (!completed || completed?.length === 0) {
        toast({
          title: 'There are no completed scorecards to insert.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      const jobId = applicationData?.job_id ?? applicationData?.ats_job?.id;
      const appId = applicationData?.id;
      const completedWithLinks = completed.map((sc: any) => ({
        ...sc,
        scorecard_url:
          jobId && appId && sc?.id
            ? `${window.location.origin}/jobs/${jobId}?appId=${appId}&currentView=scorecard&tab=candidates&scorecard_id=${sc.id}`
            : null,
      }));

      editor
        .chain()
        .focus()
        .insertContent([
          {
            type: 'ScorecardTable',
            attrs: {
              data: {
                completed: completedWithLinks,
              },
            },
          },
          {
            type: 'paragraph',
          },
        ])
        .run();
    };

    const formConfig = [
      {
        name: 'note',
        type: 'editor',
        isRequired: false,
        placeholder: 'Add a note for the approvers...',
        editorStyles: {
          minHeight: '150px',
        },
        editorContainerStyles: {
          border: 'none',
        },
        pluginsConfig: ['bold', 'italic', 'strike', 'image', 'link'],
        customExtensions: [ScorecardTableExtension],
        hideToolbar: false,
        getEditorInstance: true,
        toolbarAddon: (
          <KButton
            variant="quaternary"
            size="sm"
            icon={PlusCircle}
            onClick={handleInsertScorecardHistory}
          >
            Scorecard history
          </KButton>
        ),
      },
    ];

    const renderApproverNote = () => {
      return (
        <Box border="1px solid" borderColor="border-color" borderRadius="md">
          <KText
            p="16px 20px"
            borderBottom="1px solid"
            borderColor="border-color"
            bg="secondary-25"
            size="sm-medium"
            color="secondary-900"
          >
            Note to approvers
          </KText>
          <FormBuilder
            formRef={formRef}
            config={formConfig}
            initialValues={initialValues}
          />
        </Box>
      );
    };

    const renderApproverFlow = () => {
      return (
        <Box border="1px solid" borderColor="border-color" borderRadius="md">
          <KText
            p="16px 20px"
            borderBottom="1px solid"
            borderColor="border-color"
            bg="secondary-25"
            size="sm-medium"
            color="secondary-900"
          >
            Offer approval flow
          </KText>
          <ApprovalFlowSteps
            config={approvalData || {}}
            isLoading={isApprovalLoading}
            onClose={() => {}}
            entityType={entityType}
            entityId={entityId}
            onSuccess={() => {}}
            renderModalInParent={false}
            viewMode={true}
            offerId={offerId}
            handleModalOpen={undefined}
            handleUserSelect={undefined}
          />
        </Box>
      );
    };

    if (isApprovalLoading) {
      return (
        <Flex h="100%" w="100%" justifyContent="center" alignItems="center">
          <Spinner color="primary-600" />
        </Flex>
      );
    }

    return (
      <Flex gap="12px" flexDir="column">
        {renderApproverNote()}
        {renderApproverFlow()}
      </Flex>
    );
  }
);
